import * as THREE from 'three';
import { Kit } from '../seasons/kit.ts';

/**
 * The druid's path's trophy (docs/sendas/druida.md §10), on its own cell of the bastion, two or three hexes off the
 * paths' statues and facing out from the keep (by the keep, the keep stood in front of it, the statue's plaque fell on
 * it and the heroes idling there stood on its plinth).
 *
 * - Healed (`healed`): a living bough of the Grandsire set in a trunk of stone. The stone is cut like a stump, its bark
 *   carved in grooves, roots flaring into the plinth, moss in its folds; out of its top the bough, its bark ridged and
 *   knotted, opens into four crowns, one for each season the hero walked: spring's pale green in blossom, summer's deep
 *   green, autumn's red, winter's white with frost and icicles; a few of each fallen on the plinth.
 * - Let go (`released`): its shoot a hand high (made large for the board) in a ring of stones, a leaf of each season.
 * - In both: the slab with Olmo's last face as the hero finished it and its line under it (a painted canvas: carved
 *   in shapes it never read at the game's distance), and the stone of Hob's kiln with two faces, Olmo's and Hob's;
 *   with Kaela fallen for good, a second seedling Ceda planted for her.
 *
 * The bastion's light is warm and the red omen before a Marea tints the whole board: every piece here is lit by its
 * own colour from within (`coldLit`: part of the albedo added to the emission, a cold floor under it), so the stone
 * stays slate, the winter crown white and the spring crown green instead of all turning pink.
 *
 * Draw calls: the body (one merged mesh), the leaves (one), the slab's face (one), the halo (world.ts).
 */
export interface GroveLook {
  kind: 'healed' | 'released';
  kaela: boolean;
  at: { q: number; r: number };
  /** The line carved under Olmo's last face («Se cuida.» / «Se planta.»), already in the campaign's language. */
  line?: string;
}

/** The trophy built: its root (add it to the scene), the slab's redraw once the carved font loads, and its disposal. */
export interface GroveTrophy {
  root: THREE.Mesh;
  redraw(): void;
  dispose(): void;
}

/**
 * A material whose vertex colours also light it from within (`k` of the albedo, plus a cold `floor`): the warm key and
 * the omen's red can't turn it another colour. One compiled variant for every piece that uses it.
 */
export const coldLit = (k: number, floor: THREE.ColorRepresentation, o: THREE.MeshStandardMaterialParameters = {}): THREE.MeshStandardMaterial => {
  const m = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0.02, flatShading: true, emissive: floor, ...o });
  m.onBeforeCompile = (sh) => {
    sh.uniforms.selfLit = { value: k };
    sh.fragmentShader = sh.fragmentShader
      .replace('void main() {', 'uniform float selfLit;\nvoid main() {')
      .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\n\ttotalEmissiveRadiance += diffuseColor.rgb * selfLit;');
  };
  m.customProgramCacheKey = () => `coldLit${k}`;
  return m;
};

const SEASONS = [
  // spring: fresh pale green, white blossom
  { leaf: ['#9ee070', '#7cc85a', '#b8ee8c'], inner: '#4e9a3a' },
  // summer: deep green
  { leaf: ['#3e9a34', '#2f8a2a', '#55b044'], inner: '#22581e' },
  // autumn: red and flame
  { leaf: ['#e0542e', '#c8381f', '#ef8a34'], inner: '#7e2414' },
  // winter: white with frost, a blue shade
  { leaf: ['#e2ecf8', '#c4d8ee', '#f2f6fc'], inner: '#7f9cbc' },
] as const;

const hash = (a: number, b: number): number => {
  const x = Math.sin(a * 127.1 + b * 311.7) * 43758.5453;
  return x - Math.floor(x);
};

const UP = new THREE.Vector3(0, 1, 0);

export const buildGroveTrophy = (p: THREE.Vector3, yaw: number, big: number, t: GroveLook): GroveTrophy => {
  const kit = new Kit();
  const lit = new Kit();
  // The trophy's own frame: at p, turned by yaw, scaled by big.
  const base = new THREE.Matrix4().compose(p, new THREE.Quaternion().setFromAxisAngle(UP, yaw), new THREE.Vector3(big, big, big));
  const box = new THREE.BoxGeometry(1, 1, 1);
  const cyl = new THREE.CylinderGeometry(1, 1, 1, 10);
  const taper = new THREE.CylinderGeometry(0.72, 1, 1, 8);
  const sph = new THREE.SphereGeometry(1, 10, 7);
  const ico = new THREE.IcosahedronGeometry(1, 0);
  const leafG = new THREE.OctahedronGeometry(1, 0);
  const cone = new THREE.ConeGeometry(1, 1, 5);
  const q = new THREE.Quaternion();
  const e = new THREE.Euler();
  const L = (x: number, y: number, z: number, sx: number, sy: number, sz: number, rx = 0, ry = 0, rz = 0): THREE.Matrix4 =>
    new THREE.Matrix4().compose(new THREE.Vector3(x, y, z), q.setFromEuler(e.set(rx, ry, rz, 'XYZ')), new THREE.Vector3(sx, sy, sz));
  const put = (k: Kit, g: THREE.BufferGeometry, c: THREE.ColorRepresentation, m: THREE.Matrix4): void => k.add(g, c, base, m);
  const seg = (k: Kit, g: THREE.BufferGeometry, c: THREE.ColorRepresentation, a: THREE.Vector3, b: THREE.Vector3, r0: number, r1 = r0): void => {
    const d = b.clone().sub(a);
    const len = d.length();
    const m = new THREE.Matrix4().compose(a.clone().add(b).multiplyScalar(0.5), new THREE.Quaternion().setFromUnitVectors(UP, d.normalize()), new THREE.Vector3(r0, len, r1));
    k.add(g, c, base, m);
  };
  const V = (x: number, y: number, z: number): THREE.Vector3 => new THREE.Vector3(x, y, z);

  // The plinth: two steps of dark slate (pale stone went white on the yard's floor, pink under the omen), their edges
  // dressed lighter; along its front a band inlaid with the four seasons' leaves.
  put(kit, box, '#262b33', L(0, 0.08, 0, 2.7, 0.16, 1.5));
  put(kit, box, '#525a66', L(0, 0.165, 0, 2.74, 0.025, 1.54));
  put(kit, box, '#30363f', L(0, 0.24, -0.05, 2.35, 0.12, 1.2));
  put(kit, box, '#5a6270', L(0, 0.305, -0.05, 2.39, 0.02, 1.24));
  put(kit, box, '#262a31', L(0, 0.08, 0.752, 1.9, 0.08, 0.01));
  SEASONS.forEach((s, i) => {
    const x = -0.72 + i * 0.48;
    put(lit, leafG, s.leaf[0], L(x, 0.08, 0.758, 0.11, 0.035, 0.006, 0, 0, Math.PI / 2));
    put(lit, box, s.inner, L(x, 0.08, 0.757, 0.16, 0.008, 0.004));
  });

  if (t.kind === 'healed') {
    // The trunk of stone, cut like a stump: three drums narrowing, grooves of carved bark, roots flaring into the plinth.
    const sx = 0;
    const sz = -0.12;
    put(kit, cyl, '#59606a', L(sx, 0.52, sz, 0.46, 0.42, 0.44));
    put(kit, cyl, '#525862', L(sx, 0.86, sz, 0.41, 0.3, 0.39));
    put(kit, cyl, '#4b515b', L(sx, 1.08, sz, 0.38, 0.16, 0.36));
    // Its broken rim, a ring of teeth.
    for (let i = 0; i < 9; i++) {
      const a = (i / 9) * Math.PI * 2;
      put(kit, box, i % 2 ? '#5d646e' : '#4a5059', L(sx + Math.cos(a) * 0.33, 1.2 + hash(i, 3) * 0.06, sz + Math.sin(a) * 0.31, 0.12, 0.12 + hash(i, 4) * 0.1, 0.08, 0, -a, 0));
    }
    // Carved grooves down it.
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2 + 0.2;
      const r = 0.44 - (i % 3) * 0.005;
      seg(kit, box, '#2c3038', V(sx + Math.cos(a) * r, 0.32, sz + Math.sin(a) * r), V(sx + Math.cos(a) * (r - 0.06), 1.12, sz + Math.sin(a) * (r - 0.06)), 0.03, 0.02);
    }
    // Roots of stone flaring out into the plinth.
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + 0.4;
      seg(kit, taper, '#535a64', V(sx + Math.cos(a) * 0.62, 0.32, sz + Math.sin(a) * 0.55), V(sx + Math.cos(a) * 0.32, 0.6, sz + Math.sin(a) * 0.3), 0.07, 0.07);
    }
    // Moss in its folds.
    for (let i = 0; i < 7; i++) {
      const a = hash(i, 9) * Math.PI * 2;
      const y = 0.4 + hash(i, 10) * 0.6;
      put(lit, sph, i % 2 ? '#4f8a34' : '#3c7428', L(sx + Math.cos(a) * 0.44, y, sz + Math.sin(a) * 0.42, 0.1, 0.05, 0.08, 0, -a, 0));
    }
    // Earth in the hollow where the bough goes in.
    put(kit, sph, '#2a2018', L(sx, 1.17, sz, 0.3, 0.05, 0.28));

    // The bough: living bark, dark and ridged, knotted; it forks into four.
    const bark = '#4a3426';
    const ridge = '#2c1d14';
    const knot = '#6a4a34';
    // (The taper's top is narrower: the cylinder's top radius is 0.72 of its foot.)
    const limb = (a: THREE.Vector3, b: THREE.Vector3, r1: number): void => {
      seg(kit, taper, bark, a, b, r1, r1);
      const d = b.clone().sub(a);
      const side = new THREE.Vector3().crossVectors(d, UP).normalize();
      if (side.lengthSq() < 0.1) side.set(1, 0, 0);
      const fwd = new THREE.Vector3().crossVectors(side, d).normalize();
      for (let k = 0; k < 4; k++) {
        const ang = (k / 4) * Math.PI * 2;
        const off = side.clone().multiplyScalar(Math.cos(ang) * r1 * 0.92).add(fwd.clone().multiplyScalar(Math.sin(ang) * r1 * 0.92));
        seg(kit, box, ridge, a.clone().add(off).addScaledVector(d, 0.06), b.clone().add(off.multiplyScalar(0.75)).addScaledVector(d, -0.06), r1 * 0.22, r1 * 0.22);
      }
    };
    const root = V(sx, 1.12, sz);
    const fork = V(sx + 0.06, 1.62, sz + 0.02);
    limb(root, fork, 0.16);
    put(kit, sph, knot, L(sx + 0.13, 1.42, sz + 0.1, 0.06, 0.08, 0.04));
    put(kit, sph, ridge, L(sx + 0.14, 1.42, sz + 0.13, 0.025, 0.035, 0.02));
    // Four branches, each with its season's crown at its tip.
    const tips: [THREE.Vector3, THREE.Vector3, THREE.Vector3, number][] = [
      // [the elbow, the tip, the crown's middle, its size]
      [V(-0.32, 1.98, sz + 0.06), V(-0.62, 2.2, sz + 0.12), V(-0.68, 2.3, sz + 0.14), 0.42],
      [V(0.0, 2.08, sz - 0.08), V(-0.02, 2.5, sz - 0.08), V(0.0, 2.68, sz - 0.08), 0.46],
      [V(0.4, 1.96, sz + 0.0), V(0.66, 2.18, sz + 0.06), V(0.74, 2.28, sz + 0.08), 0.42],
      [V(0.36, 1.48, sz + 0.12), V(0.74, 1.56, sz + 0.2), V(0.82, 1.66, sz + 0.22), 0.34],
    ];
    tips.forEach(([elbow, tip], i) => {
      const from = i === 3 ? V(sx + 0.05, 1.38, sz + 0.04) : fork;
      limb(from, elbow, i === 3 ? 0.08 : 0.1);
      limb(elbow, tip, 0.06);
      // Two twigs off each.
      for (let k = 0; k < 2; k++) {
        const tw = tip.clone().add(V((hash(i, k) - 0.5) * 0.5, 0.15 + hash(k, i) * 0.15, (hash(i + 4, k) - 0.5) * 0.3));
        seg(kit, taper, bark, elbow.clone().lerp(tip, 0.6), tw, 0.03, 0.025);
      }
    });
    // The crowns: an inner mass for volume, then the season's leaves round it, each a lozenge turned its own way.
    tips.forEach(([, , mid, size], i) => {
      const s = SEASONS[i] as (typeof SEASONS)[number];
      put(lit, ico, s.inner, L(mid.x, mid.y, mid.z, size * 0.62, size * 0.5, size * 0.55, i, i * 2, 0));
      const n = 22;
      for (let k = 0; k < n; k++) {
        const u = hash(i * 31 + k, 1) * Math.PI * 2;
        const v = Math.acos(1 - 2 * (0.08 + hash(i * 31 + k, 2) * 0.72));
        const r = size * (0.62 + hash(i * 31 + k, 3) * 0.3);
        const x = mid.x + Math.sin(v) * Math.cos(u) * r;
        const y = mid.y + Math.cos(v) * r * 0.78;
        const z = mid.z + Math.sin(v) * Math.sin(u) * r;
        const c = s.leaf[k % 3] as string;
        put(lit, leafG, c, L(x, y, z, 0.075, 0.016, 0.14, hash(k, i) * 1.2 - 0.6, u, (hash(i, k + 7) - 0.5) * 1.4));
      }
      if (i === 0)
        // Spring in blossom: little white flowers, a pink heart.
        for (let k = 0; k < 8; k++) {
          const u = hash(k, 40) * Math.PI * 2;
          const x = mid.x + Math.cos(u) * size * 0.72;
          const z = mid.z + Math.sin(u) * size * 0.66;
          const y = mid.y + (hash(k, 41) - 0.3) * size * 0.8;
          for (let j = 0; j < 4; j++) put(lit, sph, '#f4f7fb', L(x + Math.cos(j * 1.57) * 0.028, y + 0.01, z + Math.sin(j * 1.57) * 0.028, 0.03, 0.012, 0.03));
          put(lit, sph, '#e88aa0', L(x, y + 0.02, z, 0.014, 0.014, 0.014));
        }
      if (i === 3) {
        // Winter: snow lying on its top, icicles off its underside.
        put(lit, sph, '#f6f9fd', L(mid.x, mid.y + size * 0.36, mid.z, size * 0.5, size * 0.14, size * 0.46));
        for (let k = 0; k < 6; k++) {
          const u = (k / 6) * Math.PI * 2;
          const m = L(mid.x + Math.cos(u) * size * 0.5, mid.y - size * 0.42, mid.z + Math.sin(u) * size * 0.45, 0.022, 0.14 + hash(k, 50) * 0.08, 0.022, Math.PI, 0, 0);
          put(lit, cone, '#cfe6ff', m);
        }
      }
    });
    // A few of each season fallen on the plinth.
    for (let k = 0; k < 12; k++) {
      const s = SEASONS[k % 4] as (typeof SEASONS)[number];
      const a = hash(k, 60) * Math.PI * 2;
      const r = 0.55 + hash(k, 61) * 0.45;
      put(lit, leafG, s.leaf[k % 3] as string, L(Math.cos(a) * r * 1.2, 0.322, sz + Math.sin(a) * r * 0.7, 0.07, 0.008, 0.12, 0, a, 0));
    }
  } else {
    // The shoot in its ring of stones: a mound of black earth, the stones round it (big enough to read), a stem, a
    // leaf of each season and its bud.
    put(kit, sph, '#241b15', L(0, 0.3, -0.08, 0.62, 0.12, 0.48));
    for (let i = 0; i < 11; i++) {
      const a = (i / 11) * Math.PI * 2;
      put(kit, ico, ['#4f5660', '#5c636d', '#454b54'][i % 3] as string, L(Math.cos(a) * 0.66, 0.38, -0.08 + Math.sin(a) * 0.48, 0.17, 0.14 + hash(i, 1) * 0.06, 0.15, a, a, 0));
    }
    seg(kit, taper, '#3e5a26', V(0, 0.32, -0.08), V(0.03, 1.25, -0.06), 0.05, 0.035);
    SEASONS.forEach((s, i) => {
      const y = 0.62 + i * 0.16;
      const k = i % 2 ? 1 : -1;
      seg(kit, cyl, '#3e5a26', V(0.01, y, -0.07), V(k * 0.16, y + 0.08, -0.07), 0.012, 0.012);
      put(lit, leafG, s.leaf[0], L(k * 0.3, y + 0.12, -0.07, 0.17, 0.03, 0.09, 0, 0, k * -0.5));
    });
    put(lit, sph, '#8fd46a', L(0.03, 1.3, -0.06, 0.06, 0.1, 0.06));
  }

  // The slab with Olmo's last face, at the front left, leaning back: dark wood framed on two feet of stone. The face is
  // the canvas below (its own mesh); here the frame.
  const slabX = -0.92;
  const slabZ = 0.36;
  const slabYaw = 0.32;
  const slabTilt = -0.16;
  const slabY = 0.92;
  const SW = 0.74;
  const SH = 1.0;
  const slab = (x: number, y: number, z: number, sx: number, sy: number, sz: number, rx = 0, ry = 0, rz = 0): THREE.Matrix4 =>
    new THREE.Matrix4()
      .compose(V(slabX, slabY, slabZ), q.setFromEuler(e.set(slabTilt, slabYaw, 0, 'YXZ')), V(1, 1, 1))
      .multiply(L(x, y, z, sx, sy, sz, rx, ry, rz));
  put(kit, box, '#21170f', slab(0, 0, -0.04, SW + 0.14, SH + 0.14, 0.07));
  for (const [x, y, w, h] of [
    [0, SH / 2 + 0.05, SW + 0.18, 0.07],
    [0, -SH / 2 - 0.05, SW + 0.18, 0.07],
    [SW / 2 + 0.05, 0, 0.07, SH + 0.18],
    [-SW / 2 - 0.05, 0, 0.07, SH + 0.18],
  ] as const)
    put(kit, box, '#3a2a1e', slab(x, y, 0.0, w, h, 0.06));
  for (const x of [-0.24, 0.24]) put(kit, box, '#4a5059', L(slabX + x * Math.cos(slabYaw), 0.4, slabZ - x * Math.sin(slabYaw) - 0.05, 0.16, 0.18, 0.22, 0, slabYaw, 0));
  // A spray of the four seasons tucked at the slab's top corner.
  SEASONS.forEach((s, i) => put(lit, leafG, s.leaf[0], slab(SW / 2 - 0.02 + (i % 2) * 0.05, SH / 2 + 0.06 - i * 0.04, 0.05, 0.05, 0.012, 0.1, 0.3, 0, -0.9 + i * 0.5)));
  const faceM = new THREE.Matrix4().multiplyMatrices(base, slab(0, 0, 0.032, 1, 1, 1));

  // The kiln's stone at the right: a block of blackened stone, soot at its top, two faces carved side by side, Olmo's
  // (leaves in its hair) and Hob's (his cap).
  const kx = 1.0;
  const kz = 0.32;
  const ky = -0.35;
  const K = (x: number, y: number, z: number, sx: number, sy: number, sz: number): THREE.Matrix4 => L(kx + x * Math.cos(ky) + z * Math.sin(ky), y, kz - x * Math.sin(ky) + z * Math.cos(ky), sx, sy, sz, 0, ky, 0);
  put(kit, box, '#34312f', K(0, 0.6, 0, 0.7, 0.58, 0.46));
  put(kit, box, '#1c1a19', K(0, 0.92, 0, 0.74, 0.07, 0.5));
  put(kit, box, '#26231f', K(0, 0.6, 0.235, 0.6, 0.46, 0.01));
  for (const [dx, tone] of [
    [-0.15, '#a49e96'],
    [0.15, '#968f86'],
  ] as const) {
    put(kit, sph, tone, K(dx, 0.6, 0.245, 0.11, 0.15, 0.03));
    for (const k of [-1, 1]) put(kit, sph, '#100e0d', K(dx + k * 0.04, 0.63, 0.27, 0.024, 0.013, 0.01));
    put(kit, box, '#100e0d', K(dx, 0.54, 0.27, 0.07, 0.012, 0.01));
    put(kit, box, '#100e0d', K(dx, 0.6, 0.272, 0.012, 0.04, 0.01));
  }
  for (const k of [-1, 1]) put(lit, leafG, '#7cc85a', K(-0.15 + k * 0.06, 0.76, 0.25, 0.05, 0.01, 0.03));
  put(kit, box, '#2a2622', K(0.15, 0.75, 0.25, 0.2, 0.05, 0.03));
  // A glow of the kiln still in it: a red-hot crack at its foot.
  put(lit, box, '#ff6a3a', K(0.05, 0.38, 0.236, 0.34, 0.016, 0.01));

  // Kaela fallen for good: a second seedling, Ceda's, by the slab.
  if (t.kaela) {
    put(kit, sph, '#241b15', L(-0.3, 0.33, 0.55, 0.18, 0.04, 0.14));
    seg(kit, taper, '#3e5a26', V(-0.3, 0.33, 0.55), V(-0.3, 0.72, 0.55), 0.025, 0.02);
    for (const k of [-1, 1]) put(lit, leafG, '#7cc85a', L(-0.3 + k * 0.09, 0.62 + (k + 1) * 0.04, 0.55, 0.1, 0.02, 0.05, 0, 0, k * -0.6));
  }
  for (const g of [box, cyl, taper, sph, ico, leafG, cone]) g.dispose();

  const mesh = kit.build(coldLit(0.6, '#141c28', { roughness: 0.95 }), 'grove-trophy') as THREE.Mesh;
  mesh.castShadow = true;
  const lm = lit.build(coldLit(0.62, '#0c1410', { roughness: 0.7, flatShading: true }), 'grove-trophy-lit');
  if (lm) {
    lm.castShadow = true;
    mesh.add(lm);
  }
  // The slab's face: Olmo's last face as the hero finished it, and its line.
  const cv = document.createElement('canvas');
  cv.width = 512;
  cv.height = 704;
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  const fm = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.8, metalness: 0, emissive: '#ffffff', emissiveMap: tex, emissiveIntensity: 0.5 });
  const face = new THREE.Mesh(new THREE.PlaneGeometry(SW, SH), fm);
  face.name = 'grove-trophy-face';
  face.matrixAutoUpdate = false;
  face.matrix.copy(faceM);
  // (The trophy's root is in world space already: its child takes its own world matrix.)
  mesh.add(face);
  const redraw = (): void => {
    olmoFace(cv, t.line ?? '', t.kind === 'healed');
    tex.needsUpdate = true;
  };
  redraw();
  return {
    root: mesh,
    redraw,
    dispose: () => {
      mesh.traverse((o) => {
        if (!(o instanceof THREE.Mesh)) return;
        o.geometry.dispose();
        (o.material as THREE.Material).dispose();
      });
      tex.dispose();
    },
  };
};

/**
 * Olmo's last face carved in the slab's wood, painted on its canvas: the grain, an old man's face in relief with
 * leaves for hair and beard, deep eyes, the mouth the hero carved, and under it the line, cut in pale letters.
 */
const olmoFace = (cv: HTMLCanvasElement, line: string, healed: boolean): void => {
  const c = cv.getContext('2d');
  if (!c) return;
  const W = cv.width;
  const H = cv.height;
  // The wood: dark, its grain running down.
  const bg = c.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#4a3324');
  bg.addColorStop(1, '#33231a');
  c.fillStyle = bg;
  c.fillRect(0, 0, W, H);
  for (let i = 0; i < 46; i++) {
    const x0 = (i / 46) * W + Math.sin(i * 2.3) * 8;
    c.strokeStyle = i % 3 ? 'rgba(20,12,8,0.35)' : 'rgba(110,80,60,0.25)';
    c.lineWidth = 1 + (i % 4);
    c.beginPath();
    c.moveTo(x0, 0);
    for (let y = 0; y <= H; y += 32) c.lineTo(x0 + Math.sin(y * 0.02 + i) * 7, y);
    c.stroke();
  }
  // A bevel round the edge.
  c.strokeStyle = 'rgba(0,0,0,0.55)';
  c.lineWidth = 18;
  c.strokeRect(9, 9, W - 18, H - 18);
  c.strokeStyle = 'rgba(140,110,90,0.35)';
  c.lineWidth = 3;
  c.strokeRect(20, 20, W - 40, H - 40);
  const cx = W / 2;
  const cy = 262;
  // Leaves for hair and beard, carved round the face: darker cuts with a pale edge.
  const leaf = (x: number, y: number, a: number, s: number, tone: string): void => {
    c.save();
    c.translate(x, y);
    c.rotate(a);
    c.beginPath();
    c.moveTo(0, -s);
    c.quadraticCurveTo(s * 0.55, 0, 0, s);
    c.quadraticCurveTo(-s * 0.55, 0, 0, -s);
    c.fillStyle = tone;
    c.fill();
    c.strokeStyle = 'rgba(18,10,6,0.85)';
    c.lineWidth = 3;
    c.stroke();
    c.beginPath();
    c.moveTo(0, -s * 0.8);
    c.lineTo(0, s * 0.8);
    c.strokeStyle = 'rgba(18,10,6,0.6)';
    c.lineWidth = 2;
    c.stroke();
    c.restore();
  };
  for (let i = 0; i < 15; i++) {
    const a = Math.PI + (i / 14) * Math.PI;
    leaf(cx + Math.cos(a) * 168, cy - 10 + Math.sin(a) * 196, a + Math.PI / 2, 40, i % 2 ? '#7a5e48' : '#8a6c54');
  }
  for (let i = 0; i < 11; i++) {
    const a = 0.15 + (i / 10) * (Math.PI - 0.3);
    leaf(cx + Math.cos(a) * 140, cy + 70 + Math.sin(a) * 150, a + Math.PI / 2, 34, i % 2 ? '#6e5440' : '#82654e');
  }
  // The face in relief: paler wood, lit from the top left.
  const fg = c.createRadialGradient(cx - 40, cy - 70, 20, cx, cy, 200);
  fg.addColorStop(0, '#b29a84');
  fg.addColorStop(1, '#6e5442');
  c.fillStyle = fg;
  c.beginPath();
  c.ellipse(cx, cy, 128, 168, 0, 0, Math.PI * 2);
  c.fill();
  c.strokeStyle = 'rgba(16,9,5,0.9)';
  c.lineWidth = 6;
  c.stroke();
  const ink = '#140b06';
  // Wrinkles on the brow.
  c.strokeStyle = 'rgba(30,18,10,0.7)';
  c.lineWidth = 4;
  for (const y of [-118, -100]) {
    c.beginPath();
    c.moveTo(cx - 62, cy + y + 6);
    c.quadraticCurveTo(cx, cy + y - 10, cx + 62, cy + y + 6);
    c.stroke();
  }
  // Heavy brows.
  c.strokeStyle = ink;
  c.lineWidth = 15;
  c.lineCap = 'round';
  for (const k of [-1, 1]) {
    c.beginPath();
    c.moveTo(cx + k * 18, cy - 62);
    c.quadraticCurveTo(cx + k * 52, cy - 84, cx + k * 92, cy - 58);
    c.stroke();
  }
  // Deep eyes: almond sockets, a glint of the living wood in each (green if healed, the dusk's grey if let go).
  for (const k of [-1, 1]) {
    c.fillStyle = ink;
    c.beginPath();
    c.ellipse(cx + k * 52, cy - 32, 30, 14, 0, 0, Math.PI * 2);
    c.fill();
    c.fillStyle = healed ? '#8fd46a' : '#a8b4c0';
    c.beginPath();
    c.arc(cx + k * 52 + 6, cy - 34, 5, 0, Math.PI * 2);
    c.fill();
  }
  // The nose: a ridge lit on one side, shadowed on the other.
  c.fillStyle = 'rgba(20,12,8,0.55)';
  c.beginPath();
  c.moveTo(cx + 4, cy - 40);
  c.lineTo(cx + 22, cy + 34);
  c.lineTo(cx - 2, cy + 40);
  c.closePath();
  c.fill();
  c.strokeStyle = 'rgba(200,180,160,0.55)';
  c.lineWidth = 4;
  c.beginPath();
  c.moveTo(cx - 6, cy - 40);
  c.lineTo(cx - 14, cy + 32);
  c.stroke();
  // Lines down the cheeks.
  c.strokeStyle = 'rgba(30,18,10,0.7)';
  c.lineWidth = 5;
  for (const k of [-1, 1]) {
    c.beginPath();
    c.moveTo(cx + k * 40, cy + 30);
    c.quadraticCurveTo(cx + k * 62, cy + 70, cx + k * 50, cy + 96);
    c.stroke();
  }
  // The mouth the hero carved: fresh, paler at its edge, closed and at peace.
  c.strokeStyle = ink;
  c.lineWidth = 12;
  c.beginPath();
  c.moveTo(cx - 46, cy + 86);
  c.quadraticCurveTo(cx, cy + 102, cx + 46, cy + 86);
  c.stroke();
  c.strokeStyle = 'rgba(232,220,206,0.8)';
  c.lineWidth = 3;
  c.beginPath();
  c.moveTo(cx - 44, cy + 96);
  c.quadraticCurveTo(cx, cy + 112, cx + 44, cy + 96);
  c.stroke();
  // The line under it, cut in pale cold letters with their shadow.
  if (line) {
    let size = 76;
    c.textAlign = 'center';
    c.textBaseline = 'middle';
    const font = (n: number): string => `${n}px "IM Fell English SC", "IM Fell English", Georgia, serif`;
    c.font = font(size);
    while (size > 30 && c.measureText(line).width > W - 70) {
      size -= 4;
      c.font = font(size);
    }
    const y = H - 108;
    c.fillStyle = 'rgba(0,0,0,0.75)';
    c.fillText(line, cx + 3, y + 4);
    c.fillStyle = '#eef3f8';
    c.fillText(line, cx, y);
    // A rule cut over it.
    c.fillStyle = 'rgba(0,0,0,0.6)';
    c.fillRect(cx - 120, y - 62, 240, 5);
    c.fillStyle = 'rgba(200,210,220,0.5)';
    c.fillRect(cx - 120, y - 58, 240, 2);
  }
};
